import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {SvgIcon} from '@valg-shared/utils/svg-icon';

export interface Pagination {
  totalItems: number;
  pageSize: number;
  currentPage: number;
}

@Component({
  selector: 'valg-paginator',
  templateUrl: './valg-paginator.component.html',
  styleUrls: [ './valg-paginator.component.scss' ],
})
export class ValgPaginatorComponent<T> implements Pagination, OnInit {
  private _totalItems = 0;
  public minPageCountForEllipsis = 8;
  public maxPageCountNextToEllipsis = 4;

  @Input()
  showPageSizeMenu: boolean = true;

  @Input()
  set totalItems(totalItems: number) {
    this._totalItems = totalItems;
    if (this.availablePages < this.currentPage) {
      this.currentPage = 1;
    }
  }

  get totalItems() {
    return this._totalItems;
  }

  @Output()
  set currentPage(val: number) {
    if (this._currentPage != val) {
      this._currentPage = val;
      this.paginationChanged.emit(this);
    }
  }

  get currentPage() {
    return this._currentPage;
  }

  private _currentPage = 1;

  @Output()
  get availablePages() {
    const pages = Math.ceil(this.totalItems / this.pageSize);
    return pages > 0 ? pages : 1;
  }

  @Input()
  set pageSize(pageSize: number) {
    if (pageSize !== this._pageSize)
    {
      const indexOfCurrentFirstItem = (this.currentPage - 1) * this._pageSize;
      const newPageContainingCurrentFirstItem = Math.floor(indexOfCurrentFirstItem / pageSize) + 1;
      this._currentPage = newPageContainingCurrentFirstItem;
      this._pageSize = pageSize;
      this.paginationChanged.emit(this);
    }
  }

  get pageSize() {
    return this._pageSize;
  }

  public paginationSteps: number[] = [ 5, 10, 20, 100 ];
  public _pageSize: number = this.paginationSteps[0];

  @Output()
  public paginationChanged: EventEmitter<Pagination> = new EventEmitter<Pagination>();

  //just an array of number for generating button
  public pages: number[] = [];
  public SvgIcon = SvgIcon;

  public SELECT_PAGE_SIZE_TEXT = 'table.select_page_size';

  ngOnInit(): void {
    this.paginationChanged.emit(this);
  }

  onNavigationChange(pageIndex: number): void {
    this.currentPage = pageIndex;
  }

  public shouldShowPagination(){
    return this._totalItems > this._pageSize;
  }

  get showEllipsis() {
    return this.availablePages >= this.minPageCountForEllipsis;
  }

  get showFirstEllipsis() {
    return this.showEllipsis && this.currentPage > this.maxPageCountNextToEllipsis;
  }

  get showSecondEllipsis() {
    return this.showEllipsis && this.currentPage <= this.availablePages - this.maxPageCountNextToEllipsis;
  }

  getSlicedPagesArray(start?: number, end?: number) {
    return Array.from(Array(this.availablePages).keys())
        .slice(start, end)
        .map((i) => i + 1);
  }

  getFirstPagesArray() {
    if (this.showFirstEllipsis) {
      return [1];
    } else if (this.showSecondEllipsis){
      return this.getSlicedPagesArray(0, this.maxPageCountNextToEllipsis + 1);
    }

    return this.getSlicedPagesArray();
  }

  getMiddlePagesArray() {
    if (this.showFirstEllipsis && this.showSecondEllipsis) {
      return [this.currentPage - 1, this.currentPage, this.currentPage + 1];
    }

    return [];
  }

  getLastPagesArray() {
    if (this.showSecondEllipsis) {
      return [this.availablePages];
    } else if (this.showFirstEllipsis){
      return this.getSlicedPagesArray(this.availablePages - this.maxPageCountNextToEllipsis - 1);
    }

    return [];
  }

  public resetPagination() {
    this.currentPage = 1;
  }
}
